import { CheckCircleIcon, PageIcon, Trash01Icon, UploadCloud02Icon, XCircleIcon } from '@vizit/icons';
import { borderColor, cva, cx } from '@vizit/theme';
import { formatFileSize } from '@vizit/utils';
import * as React from 'react';
import { Button, ButtonLevel, ButtonSize } from '../button';
import { ProgressBar } from '../progress';
import { Separator } from '../separator';
import { Text, TextColor, TextSize, TextWeight } from '../text';
import { SelectedFileStatus } from './types';

const styledFileTypes = ['application', 'image', 'video', 'text'];

interface SelectedFileProps extends React.PropsWithChildren {
  /**
   * The class name of the component.
   */
  className?: string;
  /**
   * Whether to condense the component.
   * @optional
   * @type boolean
   * @default false
   */
  condensed?: boolean;
  /**
   * The file to be displayed.
   */
  file: File;
  /**
   * The progress value of the file.
   * @optional
   * @type number
   * @default 0
   */
  progressValue?: number;
  /**
   * Whether to show the progress bar.
   * @optional
   * @type boolean
   * @default false
   */
  showProgress?: boolean;
  /**
   * The status of the file.
   */
  status?: SelectedFileStatus;
  /**
   * The function to remove the file.
   * @optional
   * @type (file: File) => void
   * @default undefined
   */
  onRemove?: (file: File) => void;
}

const styles = {
  root: cva({
    base: 'relative flex gap-4 border-solid px-12 min-w-[286px]',
    variants: {
      status: {
        [SelectedFileStatus.Ready]: 'border-secondary border',
        [SelectedFileStatus.Uploading]: 'border-secondary border',
        [SelectedFileStatus.Success]: 'border-secondary border',
        [SelectedFileStatus.Error]: 'border-error-primary border-[2px]',
      },
      condensed: {
        true: 'py-4',
        false: 'py-8',
      },
    },
  }),
  fileIcon: cva({
    base: 'px-[3px] py-1 absolute',
    variants: {
      fileType: {
        application: 'bg-error-600',
        image: 'bg-brand-600',
        video: 'bg-blue-dark-600',
        text: 'bg-success-700',
      },
    },
    defaultVariants: {
      fileType: 'image',
    },
  }),
  statusText: cva({
    base: 'flex items-start justify-center gap-2',
    variants: {
      status: {
        [SelectedFileStatus.Ready]: 'text-gray-utility-400',
        [SelectedFileStatus.Uploading]: 'text-gray-utility-400',
        [SelectedFileStatus.Success]: 'text-success-primary',
        [SelectedFileStatus.Error]: 'text-error-primary',
      },
    },
    defaultVariants: {
      status: SelectedFileStatus.Ready,
    },
  }),
};

function FileIconWithType(props: { fileType: string }) {
  const { fileType: fileTypeProp } = props;

  const splitFileType = fileTypeProp.split('/');
  const fileType = styledFileTypes.includes(splitFileType[0])
    ? (splitFileType[0] as 'application' | 'image' | 'video')
    : 'image';
  const fileTypeExtension = splitFileType.pop()?.toUpperCase();

  return (
    <div className="flex flex-col gap-2">
      <PageIcon size={40} color={borderColor.secondary} />
      <div className="relative bottom-15 right-3">
        <div className={cx(styles.fileIcon({ fileType }))}>
          <Text className="text-white text-[10px] font-bold leading-normal">{fileTypeExtension}</Text>
        </div>
      </div>
    </div>
  );
}

function SelectedFile(props: SelectedFileProps) {
  const {
    children,
    className,
    condensed = false,
    file,
    status = SelectedFileStatus.Ready,
    showProgress = false,
    progressValue = 0,
    onRemove,
    ...other
  } = props;

  const fileType = file.type;
  const fileName = file.name;
  const fileSize = formatFileSize(file.size);

  const handleRemove = React.useCallback(() => {
    if (onRemove) {
      onRemove(file);
    }
  }, [onRemove, file]);

  return (
    <div className={cx(styles.root({ status, condensed }), className)} {...other}>
      <div className="flex-shrink self-center">
        <FileIconWithType fileType={fileType} />
      </div>
      <div className="flex-grow flex justify-between max-w-[calc(100%-48px)] pr-6">
        <div className="w-full flex flex-shrink-0 flex-col gap-2 self-center pr-4">
          <Text color={TextColor.Secondary} size={TextSize.Sm}>
            {fileName}
          </Text>
          <div className="flex items-center gap-2">
            <Text color={TextColor.Tertiary} size={TextSize.Xs} className="flex-shrink-0">
              {fileSize}
            </Text>
            <div className="h-6 w-1">
              <Separator className="bg-gray-utility-300" orientation="vertical" />
            </div>
            <div className={cx(styles.statusText({ status }))}>
              {(status === SelectedFileStatus.Ready || status === SelectedFileStatus.Uploading) && (
                <UploadCloud02Icon size={16} />
              )}
              {status === SelectedFileStatus.Success && <CheckCircleIcon size={16} />}
              {status === SelectedFileStatus.Error && <XCircleIcon size={16} />}
              <Text
                color={
                  status === SelectedFileStatus.Ready || status === SelectedFileStatus.Uploading
                    ? TextColor.Quaternary
                    : TextColor.Inherit
                }
                size={TextSize.Xs}
                weight={TextWeight.Medium}
              >
                {status}
              </Text>
            </div>
            {status === SelectedFileStatus.Uploading && showProgress && (
              <div className="w-full pl-2 flex items-end justify-center">
                <ProgressBar status="success" value={progressValue} />
              </div>
            )}
          </div>
        </div>
        {children}
      </div>
      {onRemove && (
        <div className={cx('absolute right-4', condensed ? 'top-0' : 'bottom-10')}>
          <Button
            aria-label="Remove file"
            className="text-quaternary p-4 h-auto disabled:text-disabled disabled:cursor-not-allowed"
            level={ButtonLevel['Text-Secondary']}
            size={ButtonSize.Md}
            isDisabled={status === SelectedFileStatus.Uploading}
            iconStart={<Trash01Icon size={16} />}
            onPress={handleRemove}
          />
        </div>
      )}
    </div>
  );
}

export { SelectedFile };
export type { SelectedFileProps };
